import { apiClient } from './apiClient';

export interface MobileDevice {
  _id: string;
  userId: {
    _id: string;
    username: string;
    fullName: string;
  } | null;
  profileId: {
    _id: string;
    accname: string;
    accbank: string;
  } | null;
  deviceId: string;
  deviceName: string;
  deviceManufacturer: string;
  androidVersion: string;
  appVersion: string;
  lastSeen: string;
  lastHeartbeat: string;
  isActive: boolean;
  connectionMetadata: {
    ipAddress?: string;
    userAgent?: string;
    kafkaConsumerGroup?: string;
    serverUrl?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DeviceStatistics {
  totalDevices: number;
  activeDevices: number;
  devicesLast24h: number;
  devicesByManufacturer: Array<{ _id: string; count: number }>;
  devicesByAndroidVersion: Array<{ _id: string; count: number }>;
}

export interface MobileDeviceResponse {
  success: boolean;
  data: MobileDevice[];
  message: string;
}

export interface DeviceStatisticsResponse {
  success: boolean;
  data: DeviceStatistics;
  message: string;
}

class MobileDeviceService {
  /**
   * Get all active mobile devices (dev scope only)
   */
  async getAllActiveDevices(): Promise<MobileDeviceResponse> {
    try {
      const response = await apiClient.get<MobileDevice[]>('/mobile-devices/active');
      return {
        success: response.success,
        data: response.data || [],
        message: response.message || 'Active devices retrieved successfully'
      };
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get active devices for a specific profile
   */
  async getActiveDevicesForProfile(profileId: string): Promise<MobileDeviceResponse> {
    try {
      const response = await apiClient.get<MobileDevice[]>(`/mobile-devices/profile/${profileId}`);
      return {
        success: response.success,
        data: response.data || [],
        message: response.message || 'Profile devices retrieved successfully'
      };
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get device statistics (dev scope only)
   */
  async getDeviceStatistics(): Promise<DeviceStatisticsResponse> {
    try {
      const response = await apiClient.get<DeviceStatistics>('/mobile-devices/statistics');
      return {
        success: response.success,
        data: response.data || {
          totalDevices: 0,
          activeDevices: 0,
          devicesLast24h: 0,
          devicesByManufacturer: [],
          devicesByAndroidVersion: []
        },
        message: response.message || 'Device statistics retrieved successfully'
      };
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Cleanup inactive devices (dev scope only)
   */
  async cleanupInactiveDevices(olderThanHours: number = 24): Promise<{ success: boolean; data: { deletedCount: number }; message: string }> {
    try {
      const response = await apiClient.delete<{ deletedCount: number }>(`/mobile-devices/cleanup?hours=${olderThanHours}`);
      return {
        success: response.success,
        data: response.data || { deletedCount: 0 },
        message: response.message || 'Cleanup completed successfully'
      };
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Trigger an immediate transaction check on a device (dev scope only)
   */
  async triggerCheckTransaction(deviceId: string): Promise<{ success: boolean; message: string }>{
    const response = await apiClient.post(`/mobile-devices/${deviceId}/trigger-check`, {});
    return { success: !!response.success, message: response.message || 'Trigger sent' };
  }

  /**
   * Format last seen time for display
   */
  formatLastSeen(lastSeen: string): string {
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }
  }

  /**
   * Check if device is currently active (within last 5 minutes)
   */
  isDeviceActive(lastSeen: string): boolean {
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return diffMinutes < 5;
  }

  /**
   * Get status color based on last seen time
   */
  getStatusColor(lastSeen: string): 'success' | 'warning' | 'error' {
    const diffMinutes = Math.floor((new Date().getTime() - new Date(lastSeen).getTime()) / (1000 * 60));

    if (diffMinutes < 5) {
      return 'success'; // Green - Active
    } else if (diffMinutes < 30) {
      return 'warning'; // Yellow - Recently active
    } else {
      return 'error'; // Red - Inactive
    }
  }

  /**
   * Get status text based on last seen time
   */
  getStatusText(lastSeen: string): string {
    const diffMinutes = Math.floor((new Date().getTime() - new Date(lastSeen).getTime()) / (1000 * 60));

    if (diffMinutes < 5) {
      return 'Active';
    } else if (diffMinutes < 30) {
      return 'Recently Active';
    } else {
      return 'Inactive';
    }
  }
}

export const mobileDeviceService = new MobileDeviceService();
